/**
 * Loop-loan calculations.
 *
 * SPEC §4.5 (near-term snapshot), §4.6 (bull-market scenario), §4.7 (long-term loop sim).
 *
 * Vocabulary:
 *   LTV               loan / collateralValueUsd
 *   LLTV              liquidation LTV (e.g. 78%)  -  protocol parameter
 *   healthFactor      collateralUsd * LLTV / debt; >1 means safe.
 *   maxFurtherDrawdown   negative fraction; how much BTC price can drop before LTV reaches LLTV
 *   liquidationPrice  BTC price at which LTV would equal LLTV given current debt+collateral
 */

import { Decimal, ZERO, ONE, sum } from './money.js';
import type { Btc, Usd } from './money.js';
import type {
  LoopLoanScenario,
  LoopLoanSnapshot,
  LoopLoanRow,
  BtcPriceModel,
} from './types.js';
import { projectBtcPrice } from './price-model.js';

// ---------- single-state primitives ----------

export function collateralValue(btc: Btc, priceUsd: Usd): Usd {
  return btc.times(priceUsd);
}

/** Workbook C11 = C9 / D8. */
export function ltv(debtUsd: Usd, collateralUsd: Usd): Decimal {
  if (collateralUsd.isZero()) return new Decimal(Infinity);
  return debtUsd.div(collateralUsd);
}

/** Workbook C12 = (D8 * C10) / C9. */
export function healthFactor(collateralUsd: Usd, lltv: Decimal, debtUsd: Usd): Decimal {
  if (debtUsd.isZero()) return new Decimal(Infinity);
  return collateralUsd.times(lltv).div(debtUsd);
}

/** Workbook C13 = (C9/C10 - D8) / D8. Returns a negative fraction (e.g. −0.5669). */
export function maxFurtherDrawdown(debtUsd: Usd, lltv: Decimal, collateralUsd: Usd): Decimal {
  if (collateralUsd.isZero()) return ZERO;
  return debtUsd.div(lltv).minus(collateralUsd).div(collateralUsd);
}

/** Workbook C15 = C4 * (1 + C13). */
export function liquidationPrice(currentPrice: Usd, drawdown: Decimal): Usd {
  return currentPrice.times(ONE.plus(drawdown));
}

/**
 * Starting → ending collateral when the loan proceeds are used to buy more BTC at current price.
 * Workbook C8 = C7 + C9 / C4.
 */
export function endingCollateralBtc(startBtc: Btc, loanUsd: Usd, priceUsd: Usd): Btc {
  if (priceUsd.isZero()) return startBtc;
  return startBtc.plus(loanUsd.div(priceUsd));
}

/**
 * Liquidation BTC price after applying a cash buffer split between repayment and adding collateral.
 *
 * Workbook G10:
 *   = (1 + ((C9 - G8*G7)/C10 - (C8*G6 + G7*G9)) / (C8*G6 + G7*G9)) * G6
 *
 * Where G6 is the assumed-new-BTC-price, G7 is available cash, G8 is repayment fraction,
 * G9 = 1 − G8 is the collateral fraction.
 *
 * @deprecated Superseded by `downScenarioState` (Patch AA) which gives a richer
 *   post-rebalance snapshot (HF, LTV, collateral, loan, liq, ATH-drawdown-to-liq)
 *   instead of just liq price. Kept around because tests still pin the workbook
 *   formula behavior; new code should call downScenarioState.
 */
export function liquidationPriceWithCashBuffer(args: {
  loanUsd: Usd;
  lltv: Decimal;
  endingCollateralBtc: Btc;
  /** Assumed BTC price at which the cash is deployed (workbook G6). */
  assumedPriceUsd: Usd;
  availableCashUsd: Usd;
  cashUsedForRepaymentPct: Decimal; // 0..1
}): Usd {
  const cashForCollateralPct = ONE.minus(args.cashUsedForRepaymentPct);
  const numerator = args.loanUsd.minus(args.cashUsedForRepaymentPct.times(args.availableCashUsd)).div(args.lltv);
  const denominator = args.endingCollateralBtc
    .times(args.assumedPriceUsd)
    .plus(args.availableCashUsd.times(cashForCollateralPct));
  if (denominator.isZero()) return ZERO;
  const factor = ONE.plus(numerator.minus(denominator).div(denominator));
  return factor.times(args.assumedPriceUsd);
}

// ---------- BTC down-scenario ----------

export interface DownScenarioState {
  /** BTC collateral after deploying cash (added at downPrice). */
  collateralBtc: Btc;
  /** USD value of collateral at the down price. */
  collateralUsd: Usd;
  /** Loan after applying cash repayment. */
  loanUsd: Usd;
  /** LTV at the down price. */
  ltv: Decimal;
  /** Health factor at the down price. */
  healthFactor: Decimal;
  /** Liquidation BTC price after this rebalance. */
  liquidationPriceUsd: Usd;
  /** Drawdown from ATH to liquidation price (negative fraction, e.g. -0.65). */
   athDrawdownToLiqPct: Decimal;
  /** Cash split actually deployed: USD to repay, USD to add as collateral. */
  cashRepayUsd: Usd;
  cashCollateralUsd: Usd;
}

/**
 * Computes the full position state after BTC drops to `downPrice`, the user
 * deploys `availableCashUsd` total, splitting it `cashUsedForRepaymentPct` to
 * loan repayment and the remainder to buying more BTC at the down price as
 * additional collateral.
 *
 * Inputs are the "ending" state from the snapshot (post-loop): the user has
 * already taken the loan and bought the matched BTC. This scenario asks:
 * "If BTC drops from here, and I deploy this cash now, where does my
 * position stand?"
 */
export function downScenarioState(args: {
  endingCollateralBtc: Btc;
  loanUsd: Usd;
  lltv: Decimal;
  downPriceUsd: Usd;
  availableCashUsd: Usd;
  cashUsedForRepaymentPct: Decimal; // 0..1
  athReferencePriceUsd: Usd;
}): DownScenarioState {
  const repayPct = Decimal.max(0, Decimal.min(1, args.cashUsedForRepaymentPct));
  const collateralPct = ONE.minus(repayPct);

  const cashRepayUsd = args.availableCashUsd.times(repayPct);
  const cashCollateralUsd = args.availableCashUsd.times(collateralPct);

  // Loan after repayment can't go below zero.
  const loanAfter = Decimal.max(0, args.loanUsd.minus(cashRepayUsd)) as Usd;

  // BTC bought with the collateral portion, at the down price.
  const btcAdded = args.downPriceUsd.isZero() ? ZERO : cashCollateralUsd.div(args.downPriceUsd);
  const collateralBtcAfter = args.endingCollateralBtc.plus(btcAdded);
  const collateralUsdAfter = collateralBtcAfter.times(args.downPriceUsd);

  const ltvAfter = ltv(loanAfter, collateralUsdAfter);
  const hfAfter = healthFactor(collateralUsdAfter, args.lltv, loanAfter);

  // Liquidation price after this rebalance  -  derived from the post-rebalance
  // collateral & loan (NOT the workbook G10 formula, which assumed the rebalance
  // had just happened at `assumedPriceUsd`; here we already moved BTC to the down
  // price, so it's simpler:  liqPrice = loan / (collateralBtc * lltv) ).
  const liqPrice = collateralBtcAfter.isZero() || args.lltv.isZero()
    ? ZERO
    : loanAfter.div(collateralBtcAfter.times(args.lltv)) as Usd;

  const athDrawdownToLiqPct = args.athReferencePriceUsd.isZero()
    ? ZERO
    : liqPrice.minus(args.athReferencePriceUsd).div(args.athReferencePriceUsd);

  return {
    collateralBtc: collateralBtcAfter,
    collateralUsd: collateralUsdAfter,
    loanUsd: loanAfter,
    ltv: ltvAfter,
    healthFactor: hfAfter,
    liquidationPriceUsd: liqPrice,
    athDrawdownToLiqPct,
    cashRepayUsd,
    cashCollateralUsd,
  };
}

/**
 * Solver: scans `cashUsedForRepaymentPct` from 0..1 in steps and returns
 * the split that minimises the post-rebalance liquidation price.
 *
 * Why a scan and not closed-form: the liq-price function is a
 * piecewise-rational of the repay fraction (with a kink at loan=0 and a
 * potential div-by-zero at collateral=0), and the optimum is usually
 * interior. A 1% step over [0,1] = 101 evaluations, each ~10 Decimal ops:
 * trivially fast and avoids edge-case math.
 */
export function solveOptimalRepaymentSplit(args: {
  endingCollateralBtc: Btc;
  loanUsd: Usd;
  lltv: Decimal;
  downPriceUsd: Usd;
  availableCashUsd: Usd;
  athReferencePriceUsd: Usd;
  /** Granularity in [0,1]. Default 0.01 = 1%. */
  step?: number;
}): { bestPct: Decimal; bestState: DownScenarioState } {
  const step = args.step ?? 0.01;
  let bestPct = ZERO;
  let bestLiq: Usd | null = null;
  let bestState: DownScenarioState | null = null;

  for (let p = 0; p <= 1.0001; p += step) {
    const pct = new Decimal(Math.min(1, p));
    const state = downScenarioState({
      endingCollateralBtc: args.endingCollateralBtc,
      loanUsd: args.loanUsd,
      lltv: args.lltv,
      downPriceUsd: args.downPriceUsd,
      availableCashUsd: args.availableCashUsd,
      cashUsedForRepaymentPct: pct,
      athReferencePriceUsd: args.athReferencePriceUsd,
    });
    // Skip degenerate states where collateral is zero (liq price = 0 trivially).
    if (state.collateralBtc.isZero()) continue;
    if (bestLiq === null || state.liquidationPriceUsd.lessThan(bestLiq)) {
      bestLiq = state.liquidationPriceUsd;
      bestPct = pct;
      bestState = state;
    }
  }

  // Fallback: if no valid scan point (e.g. all collateral=0), return the 0% case.
  if (bestState === null) {
    bestState = downScenarioState({
      endingCollateralBtc: args.endingCollateralBtc,
      loanUsd: args.loanUsd,
      lltv: args.lltv,
      downPriceUsd: args.downPriceUsd,
      availableCashUsd: args.availableCashUsd,
      cashUsedForRepaymentPct: ZERO,
      athReferencePriceUsd: args.athReferencePriceUsd,
    });
    bestPct = ZERO;
  }

  return { bestPct, bestState };
}

// ---------- composite snapshot ----------

/** Workbook near-term block (C7..C15, D7..D8). Pure function from the user's inputs. */
export function loopLoanSnapshot(args: {
  startingCollateralBtc: Btc;
  startingLoanUsd: Usd;
  btcPriceUsd: Usd;
  lltv: Decimal;
  athReferencePriceUsd: Usd;
}): LoopLoanSnapshot {
  const startingCollateralUsd = collateralValue(args.startingCollateralBtc, args.btcPriceUsd);
  const endingBtc = endingCollateralBtc(args.startingCollateralBtc, args.startingLoanUsd, args.btcPriceUsd);
  const endingUsd = collateralValue(endingBtc, args.btcPriceUsd);
  const ltvNow = ltv(args.startingLoanUsd, endingUsd);
  const hf = healthFactor(endingUsd, args.lltv, args.startingLoanUsd);
  const drawdown = maxFurtherDrawdown(args.startingLoanUsd, args.lltv, endingUsd);
  const liqPrice = liquidationPrice(args.btcPriceUsd, drawdown);
  const totalAthDrawdown = args.athReferencePriceUsd.isZero()
    ? ZERO
    : liqPrice.minus(args.athReferencePriceUsd).div(args.athReferencePriceUsd);
  return {
    startingCollateralBtc: args.startingCollateralBtc,
    startingCollateralUsd,
    endingCollateralBtc: endingBtc,
    endingCollateralUsd: endingUsd,
    loanUsd: args.startingLoanUsd,
    ltv: ltvNow,
    healthFactor: hf,
    maxFurtherDrawdown: drawdown,
    liquidationPriceUsd: liqPrice,
    totalDrawdownFromAth: totalAthDrawdown,
  };
}

// ---------- repayment & loan-growth helpers ----------

/**
 * Yearly repayment series for years 1..n with no add-on principal  -  just interest growth + repayment.
 *
 * - 'fixed' (corrected): zero out once balance can't service repayment.
 *     next = max(0, prev*(1+apr) - repay)
 * - 'workbook' (preserves bug B1): uses `IF(prev*(1+apr) - C24*12 > 0, prev*(1+apr) - C24, 0)`
 *   which is what the spreadsheet actually does. Loan rarely zeros out.
 */
export function projectAnnualRepayment(args: {
  initialLoanUsd: Usd;
  loanApr: Decimal;
  yearlyRepaymentUsd: Usd;
  years: number;
  formula: 'fixed' | 'workbook';
}): Usd[] {
  const series: Usd[] = [];
  let balance = args.initialLoanUsd;
  for (let y = 1; y <= args.years; y++) {
    const grown = balance.times(ONE.plus(args.loanApr));
    if (y === 1) {
      // Workbook C26: simple `prev*(1+r) - repay`, no condition guard.
      balance = grown.minus(args.yearlyRepaymentUsd);
    } else if (args.formula === 'workbook') {
      // C27+: IF(grown - repay*12 > 0, grown - repay, 0)
      const cond = grown.minus(args.yearlyRepaymentUsd.times(12));
      balance = cond.greaterThan(0) ? grown.minus(args.yearlyRepaymentUsd) : ZERO;
    } else {
      // 'fixed': zero out only when payoff would push balance negative.
      const next = grown.minus(args.yearlyRepaymentUsd);
      balance = next.lessThan(0) ? ZERO : next;
    }
    series.push(balance);
  }
  return series;
}

// ---------- bull-market scenario ----------

export interface BullMarketResult {
  originalLtv: Decimal;
  newLoanUsd: Usd;
  newCollateralBtc: Btc;
  newLtv: Decimal;
  maxDrawdown: Decimal;
  liquidationPriceUsd: Usd;
}

/**
 * Workbook G21..G27. Given a higher BTC price and a planned loan-increase, model the
 * post-add state assuming the borrowed USD buys more BTC at the new price.
 */
export function bullMarketScenario(args: {
  endingCollateralBtc: Btc;       // C8
  initialLoanUsd: Usd;            // C9
  lltv: Decimal;                  // C10
  newBtcPriceUsd: Usd;            // G20
  loanIncreaseUsd: Usd;           // G22
}): BullMarketResult {
  const oldUsd = args.endingCollateralBtc.times(args.newBtcPriceUsd);
  const originalLtv = ltv(args.initialLoanUsd, oldUsd);
  const newLoan = args.initialLoanUsd.plus(args.loanIncreaseUsd);
  const newCollateralBtc = args.endingCollateralBtc.plus(args.loanIncreaseUsd.div(args.newBtcPriceUsd));
  const newCollateralUsd = newCollateralBtc.times(args.newBtcPriceUsd);
  const newLtvVal = ltv(newLoan, newCollateralUsd);
  const drawdown = maxFurtherDrawdown(newLoan, args.lltv, newCollateralUsd);
  const liqPrice = liquidationPrice(args.newBtcPriceUsd, drawdown);
  return {
    originalLtv,
    newLoanUsd: newLoan,
    newCollateralBtc,
    newLtv: newLtvVal,
    maxDrawdown: drawdown,
    liquidationPriceUsd: liqPrice,
  };
}

// ---------- long-term loop simulation ----------

/**
 * Compute the additional loan amount needed to bring the AFTER-loop LTV up to the target,
 * assuming the borrowed USD buys more BTC at the current price (so collateral USD also grows by J).
 *
 * Workbook J44 = IFS(year >= stop, 0; LTV >= target, 0; default, (target*G - H)/(1 - target)).
 *
 * Solving target = (H+J)/(G+J) for J yields J = (target*G - H)/(1 - target).
 */
export function loopBorrowAmountToTargetLtv(args: {
  year: number;
  stopLoopingBeforeYear: number;
  collateralUsdBefore: Usd;
  loanBefore: Usd;
  ltvBefore: Decimal;
  targetLtv: Decimal;
}): Usd {
  if (args.year >= args.stopLoopingBeforeYear) return ZERO;
  if (args.ltvBefore.greaterThanOrEqualTo(args.targetLtv)) return ZERO;
  const numerator = args.targetLtv.times(args.collateralUsdBefore).minus(args.loanBefore);
  const denominator = ONE.minus(args.targetLtv);
  if (denominator.isZero()) return ZERO;
  return numerator.div(denominator);
}

/**
 * Patch AL: bull-market rally event for the long-term projection.
 *
 * Models the user choosing to take an additional loan when BTC has rallied
 * to a target price. Fires exactly once, in the first projected year where
 * `btcPrice >= triggerPriceUsd`. The loan-increase USD is fully deployed
 * to buy more BTC at that year's price (mirrors workbook G21..G27 logic),
 * raising both collateral and loan together.
 *
 * Distinct from the existing "loop" mechanic (loopBorrowAmountToTargetLtv):
 * looping is a continuous LTV-targeting mechanic gated by stopLoopingBeforeYear
 * and targetLtv. The rally event is a one-shot opportunistic borrow keyed
 * to a price threshold, independent of LTV maintenance. Both can run in
 * the same simulation; their loan adds sum into LoopLoanRow.loopLoanAdd.
 */
export interface BullRallyEvent {
  /** BTC price threshold; rally fires the first projected year where price >= this. */
  triggerPriceUsd: Usd;
  /** USD added to loan; all converts to collateral at that year's BTC price. */
  loanIncreaseUsd: Usd;
}

/**
 * Long-term loop simulator.
 *
 * Workbook B43..O51 (rows 44..51 = years 1..8). Reproduces the exact recurrence:
 *   E_y = K_{y-1} + F_y           (collateral BTC before, with outside purchase added)
 *   G_y = E_y * D_y               (collateral USD before)
 *   H_y = M_{y-1} * (1 + apr)     (loan with 1yr interest)
 *   I_y = H_y / G_y               (LTV before)
 *   J_y = loopBorrowAmountToTargetLtv(...)
 *   L_y = G_y + J_y               (collateral USD after)
 *   K_y = L_y / D_y               (collateral BTC after; note J is converted at price D)
 *   M_y = H_y + J_y               (loan after)
 *   N_y = M_y / L_y               (LTV after  -  should equal target while looping)
 *   O_y = (M_y / lltv - L_y) / L_y   (max drawdown after)
 *
 * Year 1's E_1 starts from the initial post-loop collateral (workbook C37 = C8) plus F_1.
 * Year 1's H_1 starts from initial loan grown by 1yr of interest (workbook C39 * (1 + C40)).
 *
 * Patch AK: optional `priceFn` parameter. When supplied, replaces the
 * default `projectBtcPrice(date, priceModel)` lookup with the caller's
 * own date→price mapping. Used by the LoopLoan page to drive the chart
 * with one of three projection modes (model / model-from-current /
 * custom-growth) without forcing those modes through the BtcPriceModel
 * shape. The 'custom' mode in particular isn't expressible as a power
 * law, so it has to come in as a callable. Mirrors how carry-retirement
 * already accepts a priceFn from `makePriceFn` in projection-modes.ts.
 *
 * Patch AL: optional `bullRally` parameter. When supplied, fires once at
 * the first year where btcPrice >= triggerPriceUsd, adding loanIncreaseUsd
 * to the loan and converting it to BTC at that year's price. Independent
 * of the looping mechanic above; both contribute to loopLoanAdd in the
 * row and to the after-state collateral/loan.
 *
 * Engine math is unchanged when both optional params are undefined; the
 * only behavioral difference when priceFn is supplied is the source of
 * `btcPrice` per year, and when bullRally is supplied is the one-time
 * loan addition. LTV/HF/liquidation formulas downstream consume these
 * the same way regardless of source.
 *
 * Backwards compatible: existing callers passing only the first three
 * arguments fall through to projectBtcPrice and no rally event.
 */
export function projectLoopLoan(
  scenario: LoopLoanScenario,
  priceModel: BtcPriceModel,
  initialState: { postLoopCollateralBtc: Btc; loanUsd: Usd },
  priceFn?: (date: Date) => Usd,
  bullRally?: BullRallyEvent,
): LoopLoanRow[] {
  const rows: LoopLoanRow[] = [];
  let prevCollateralBtcAfter = initialState.postLoopCollateralBtc;
  let prevLoanAfter = initialState.loanUsd;
  // Patch AL: rally fires at most once across the projection. After fire,
  // the flag stays true so subsequent years don't re-trigger even if the
  // price stays above threshold (which it will, on a monotone-up model).
  let rallyFired = false;

  for (let y = 1; y <= scenario.projectionYears; y++) {
    const yearOffset = y; // workbook anchors year 1 = startDate (which itself is +1yr from the snapshot anchor in the source spreadsheet)
    const date = addYears(scenario.startDate, yearOffset);
    // Patch AK: priceFn override path. When undefined, fall back to the
    // canonical workbook-style power-law lookup. Same return shape either way.
    const btcPrice = priceFn ? priceFn(date) : projectBtcPrice(date, priceModel);

    const outsideBtc = scenario.outsidePurchasesByYear.find(p => p.year === y)?.btc ?? ZERO;
    const collateralBtcBefore = prevCollateralBtcAfter.plus(outsideBtc);
    const collateralUsdBefore = collateralBtcBefore.times(btcPrice);
    const loanBefore = prevLoanAfter.times(ONE.plus(scenario.loanApr));
    const ltvBefore = ltv(loanBefore, collateralUsdBefore);

    const loopAdd = loopBorrowAmountToTargetLtv({
      year: y,
      stopLoopingBeforeYear: scenario.stopLoopingBeforeYear,
      collateralUsdBefore,
      loanBefore,
      ltvBefore,
      targetLtv: scenario.targetLtv,
    });

    // Patch AL: bull rally event. Independent of looping.
    let rallyAdd: Usd = ZERO;
    if (bullRally && !rallyFired && btcPrice.greaterThanOrEqualTo(bullRally.triggerPriceUsd)) {
      rallyAdd = bullRally.loanIncreaseUsd;
      rallyFired = true;
    }

    // Total loan add this year combines any continuous-loop add with the
    // one-time rally add. Both convert to collateral at this year's price.
    const totalAdd = loopAdd.plus(rallyAdd);

    const collateralUsdAfter = collateralUsdBefore.plus(totalAdd);
    const collateralBtcAfter = collateralUsdAfter.div(btcPrice);
    const loanAfter = loanBefore.plus(totalAdd);
    const ltvAfter = ltv(loanAfter, collateralUsdAfter);
    const hfAfter = healthFactor(collateralUsdAfter, scenario.lltv, loanAfter);
    const maxDrawdownAfter = maxFurtherDrawdown(loanAfter, scenario.lltv, collateralUsdAfter);
    const liqPriceAfter = liquidationPrice(btcPrice, maxDrawdownAfter);

    rows.push({
      year: y,
      date,
      btcPriceUsd: btcPrice,
      collateralBtcBefore,
      outsideBtcPurchased: outsideBtc,
      collateralUsdBefore,
      loanBefore,
      ltvBefore,
      // Stored as combined for backwards compatibility with the existing
      // table column. Pre-AL this was loopAdd alone; post-AL it includes
      // any rally-event addition for the year. Tests that pre-date AL
      // never pass bullRally, so rallyAdd stays ZERO and the value is
      // identical to pre-AL.
      loopLoanAdd: totalAdd,
      collateralBtcAfter,
      collateralUsdAfter,
      loanAfter,
      ltvAfter,
      healthFactorAfter: hfAfter,
      maxDrawdownAfter,
      liquidationPriceAfter: liqPriceAfter,
    });

    prevCollateralBtcAfter = collateralBtcAfter;
    prevLoanAfter = loanAfter;
  }

  return rows;
}

/** Add `years` years to a date, preserving time-of-day (matches workbook's annual increments). */
function addYears(date: Date, years: number): Date {
  const d = new Date(date.getTime());
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return d;
}

/** Net worth without the additional loops (workbook I40):
 *    SUM(C8, F44..F51) * D51 - C9 * (1+apr)^projectionYears
 * = (initial post-loop BTC + sum of outside purchases) * final BTC price - initial loan compounded.
 */
export function netWorthWithoutAddedLoops(args: {
  initialPostLoopBtc: Btc;
  outsidePurchases: Btc[];
  finalBtcPrice: Usd;
  initialLoanUsd: Usd;
  loanApr: Decimal;
  projectionYears: number;
}): Usd {
  const totalBtc = args.initialPostLoopBtc.plus(sum(args.outsidePurchases));
  const compoundedLoan = args.initialLoanUsd.times(ONE.plus(args.loanApr).pow(args.projectionYears));
  return totalBtc.times(args.finalBtcPrice).minus(compoundedLoan);
}
